import React, { useState } from "react";
import { ChevronDown, HelpCircle as HelpCircleIcon } from "lucide-react";

/* -----------------------------------------------------------------------------
 * GameFaq — collapsible FAQ shown at the bottom of the packages view.
 * Content is generated per-game using the game's credential type + id label.
 * --------------------------------------------------------------------------- */
export function GameFaq({ game }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(prev => (prev === i ? null : i));

  const isFF = /free ?fire/i.test(game.name || "");
  const isRoblox = /roblox/i.test(game.name || "");
  const credType = game.credentialType || "id";

  // Answers vary by credential type (ID vs username+password) and by game.
  const howToFindId = credType === "username_password"
    ? `Necesitas tu usuario y contraseña de ${game.name}. El usuario es el mismo con el que inicias sesión en el juego. Si no lo recuerdas, ábrelo desde el launcher y revisa tu perfil.`
    : isFF
      ? "Abre Free Fire → toca tu avatar (arriba a la izquierda) → verás tu UID debajo del nickname. Cópialo y pégalo aquí; nosotros verificamos tu nickname real en tiempo real antes de recargar."
      : isRoblox
        ? "Abre Roblox → toca tu foto de perfil → tu username aparece en la parte superior. Escríbelo exacto (respetando mayúsculas y guiones bajos)."
        : `Abre ${game.name} → busca tu perfil o cuenta dentro del juego → allí encontrarás tu ${game.idLabel || "ID de jugador"}. Pégalo exactamente como aparece.`;

  const faqs = [
    {
      q: `¿Cómo encuentro mi ${credType === "username_password" ? "usuario y contraseña" : (game.idLabel || "ID de jugador")}?`,
      a: howToFindId,
    },
    {
      q: "¿Cuánto tarda la entrega?",
      a: "El 95% de las recargas se acreditan en menos de 5 minutos tras confirmar tu pago. Casos con verificación adicional pueden tomar hasta 30 minutos. Recibirás una notificación en tu panel apenas se acredite.",
    },
    {
      q: "¿Qué pasa si escribo mal mi ID / usuario?",
      a: "El sistema valida en tiempo real (cuando aplica) y también nuestro equipo revisa antes de recargar. Si el ID es inválido tu pedido queda en pausa y te contactamos por el chat de soporte del pedido — nunca perderás tu dinero.",
    },
    {
      q: "¿Qué métodos de pago aceptan?",
      a: "Pago Móvil, Transferencia bancaria y USD (Zinli, Binance Pay, otras plataformas activadas por el admin). Al elegir el paquete verás en pantalla los métodos disponibles con sus datos exactos.",
    },
  ];

  return (
    <section
      data-testid="game-faq-section"
      className="pt-8 border-t border-zinc-800/60"
    >
      <div className="flex items-center gap-2 mb-4">
        <HelpCircleIcon className="w-5 h-5 text-cyan-400" />
        <h4 className="text-sm sm:text-base font-black text-white tracking-widest uppercase">Preguntas Frecuentes</h4>
      </div>
      <div className="space-y-2 max-w-3xl">
        {faqs.map((f, i) => {
          const open = openIdx === i;
          return (
            <div
              key={f.q}
              data-testid={`faq-item-${i}`}
              className="rounded-xl bg-[#0f1015] border border-zinc-800 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggle(i)}
                data-testid={`faq-toggle-${i}`}
                aria-expanded={open}
                className="w-full flex justify-between items-center gap-3 px-4 py-3 text-left hover:bg-zinc-900/40 transition-colors"
              >
                <span className="text-xs sm:text-sm font-bold text-white">{f.q}</span>
                <ChevronDown
                  className={`w-4 h-4 text-cyan-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}
                />
              </button>
              {open && (
                <div
                  data-testid={`faq-answer-${i}`}
                  className="px-4 pb-3 pt-1 text-[12px] text-zinc-400 leading-relaxed border-t border-zinc-800/60"
                >
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
